// CollateralDetail.jsx — single collateral with map, hazard breakdown, climate signals
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Paper, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Divider, IconButton, Tooltip,
} from "@mui/material";
import { ArrowLeft, RefreshCw, ExternalLink, ThermometerSun, CloudRain } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import ScenarioToggle from "../components/ScenarioToggle";
import { collateralsApi, riskApi, signalsApi } from "../api/api";
import {
  fmtINR, fmtScore, fmtBps, HAZARD_LABELS, HAZARD_COLORS, riskColor,
} from "../utils/format";

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function CollateralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [scoresAll, setScoresAll] = useState({}); // {scenario -> score}
  const [scenario, setScenario] = useState("current");
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState(null);
  const [rescoring, setRescoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await collateralsApi.get(id);
      setData(data);
      const scoresArr = data.scores || [];
      const map = {};
      scoresArr.forEach((s) => { map[s.scenario] = s; });
      setScoresAll(map);

      // Fetch signals
      if (data.collateral?.latitude && data.collateral?.longitude) {
        signalsApi.signals(data.collateral.latitude, data.collateral.longitude)
          .then((r) => setSignals(r.data.signals)).catch(() => {});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const rescore = async () => {
    setRescoring(true);
    try {
      await Promise.all(["current", "2030", "2050"].map((sc) =>
        riskApi.collateral(id, sc)
      ));
      toast.success("Re-scored across all scenarios");
      load();
    } catch (e) { toast.error("Re-score failed"); }
    finally { setRescoring(false); }
  };

  if (loading) {
    return <DashboardLayout title="Collateral"><Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box></DashboardLayout>;
  }

  const c = data?.collateral || {};
  const b = data?.borrower || {};
  const s = scoresAll[scenario] || {};
  const hazards = s.hazards || [];

  return (
    <DashboardLayout
      title={c.name || "Collateral"}
      subtitle={`${c.type?.toUpperCase().replace("_", " ")} · ${c.city || ""} · PIN ${c.pin_code || "—"}`}
      headerExtra={
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button size="small" variant="contained" onClick={rescore} disabled={rescoring}
            startIcon={rescoring ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <RefreshCw size={13} />}
            data-testid="rescore-collateral-btn"
            sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}>
            {rescoring ? "Re-scoring" : "Re-score 3 scenarios"}
          </Button>
          <Button size="small" variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowLeft size={13} />}>
            Back
          </Button>
        </Box>
      }
    >
      {/* Header summary */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.3fr 1fr" }, gap: 2 }}>
        {/* Risk + map */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper sx={{ p: 2.6 }} data-testid="collateral-risk-card">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.5 }}>
              <Box>
                <Box className="cb-display" sx={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                  Physical Risk · {scenario}
                </Box>
                <Box sx={{ fontSize: 12, color: "#6B7280" }}>
                  Computed against 11 hazards & {scenario} severity layers
                </Box>
              </Box>
              <RiskPill level={s.risk_level} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              <Box>
                <Box className="cb-kpi-label">Overall Score</Box>
                <Box className="cb-mono" sx={{ fontSize: 28, fontWeight: 600, color: "#111827" }}>{fmtScore(s.overall_score)}</Box>
              </Box>
              <Box>
                <Box className="cb-kpi-label">Financial Exposure</Box>
                <Box className="cb-mono" sx={{ fontSize: 22, fontWeight: 600, color: "#111827" }}>{fmtINR(s.financial_exposure_inr)}</Box>
              </Box>
              <Box>
                <Box className="cb-kpi-label">PD Uplift</Box>
                <Box className="cb-mono" sx={{ fontSize: 22, fontWeight: 600, color: "#8F5E14" }}>{fmtBps(s.pd_uplift_bps)}</Box>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 0, overflow: "hidden" }}>
            <Box sx={{ height: 380 }}>
              <MapContainer center={[c.latitude, c.longitude]} zoom={9} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                />
                <Marker position={[c.latitude, c.longitude]}>
                  <Popup>
                    <b>{c.name}</b><br />
                    {c.address || c.city || ""}<br />
                    PIN {c.pin_code}
                  </Popup>
                </Marker>
                <CircleMarker
                  center={[c.latitude, c.longitude]}
                  radius={18}
                  pathOptions={{
                    color: riskColor(s.risk_level || "Low"),
                    fillColor: riskColor(s.risk_level || "Low"),
                    fillOpacity: 0.25,
                    weight: 2,
                  }}
                />
              </MapContainer>
            </Box>
          </Paper>
        </Box>

        {/* Side: meta + signals */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper sx={{ p: 2.4 }}>
            <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
              Collateral Attributes
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <Meta label="Borrower" value={b.name || "—"} link={`/borrowers/${b.borrower_id || ""}`} />
              <Meta label="CIF" value={b.cif_no || "—"} />
              <Meta label="Type" value={c.type?.replace("_", " ")} />
              <Meta label="Construction" value={(c.construction_type || "—").toUpperCase()} />
              <Meta label="Valuation" value={fmtINR(c.current_valuation_inr)} />
              <Meta label="Insurance" value={fmtINR(c.insurance_cover_inr)} />
              <Meta label="LTV" value={`${c.ltv_pct || 0}%`} />
              <Meta label="Asset Age" value={`${c.asset_age_years || 0}y`} />
              <Meta label="Elevation" value={c.elevation_m != null ? `${c.elevation_m}m` : "—"} />
              <Meta label="Coastal Dist." value={c.coastal_distance_km != null ? `${c.coastal_distance_km}km` : "—"} />
              <Meta label="Latitude" value={Number(c.latitude || 0).toFixed(6)} mono />
              <Meta label="Longitude" value={Number(c.longitude || 0).toFixed(6)} mono />
            </Box>
          </Paper>

          <Paper sx={{ p: 2.4 }} data-testid="climate-signals-card">
            <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
              Live Climate Signals
            </Box>
            {!signals ? <Box sx={{ fontSize: 12, color: "#94A3B8" }}>Fetching live data…</Box> :
              (() => {
                const cur = signals.open_meteo?.current || {};
                const daily = signals.open_meteo?.daily || {};
                const tmax = daily.temperature_2m_max?.[0];
                const tmin = daily.temperature_2m_min?.[0];
                const psum = daily.precipitation_sum?.[0];
                const projection = signals.climate_projection;
                return (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.4 }}>
                    <Meta label="Now" value={cur.temperature_2m != null ? `${cur.temperature_2m}°C` : "—"} icon={<ThermometerSun size={12} />} />
                    <Meta label="Humidity" value={cur.relative_humidity_2m != null ? `${cur.relative_humidity_2m}%` : "—"} />
                    <Meta label="Today max/min" value={tmax != null ? `${tmax}° / ${tmin}°C` : "—"} />
                    <Meta label="Today rain" value={psum != null ? `${psum} mm` : "—"} icon={<CloudRain size={12} />} />
                    {projection && (
                      <>
                        <Meta label="2024-2050 avg T" value={projection.avg_temperature_c != null ? `${projection.avg_temperature_c}°C` : "—"} />
                        <Meta label="2024-2050 rain" value={`${(projection.total_precip_mm / 1000).toFixed(1)} k mm`} />
                      </>
                    )}
                  </Box>
                );
              })()
            }
          </Paper>
        </Box>
      </Box>

      {/* Hazard breakdown */}
      <Paper sx={{ mt: 2, overflow: "hidden" }} data-testid="hazard-breakdown">
        <Box sx={{ p: 2.2, borderBottom: "1px solid #F0EFEA" }}>
          <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>Per-Hazard Breakdown · {scenario}</Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hazard</TableCell>
              <TableCell align="right">Exposure</TableCell>
              <TableCell align="right">Vulnerability</TableCell>
              <TableCell align="right">Consequence</TableCell>
              <TableCell align="right">Overall</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell>Risk-mitigation guidance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hazards.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#94A3B8" }}>
                No hazard data — click "Re-score 3 scenarios".
              </TableCell></TableRow>
            )}
            {hazards.map((h) => (
              <TableRow key={h.hazard_id}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: HAZARD_COLORS[h.hazard_id] || "#6B7280" }} />
                    <Box sx={{ fontWeight: 600, fontSize: 13 }}>{HAZARD_LABELS[h.hazard_id] || h.hazard_name}</Box>
                  </Box>
                </TableCell>
                <TableCell align="right" className="cb-mono">{fmtScore(h.exposure_score)}</TableCell>
                <TableCell align="right" className="cb-mono">{fmtScore(h.vulnerability_score)}</TableCell>
                <TableCell align="right" className="cb-mono">{fmtScore(h.consequence_score)}</TableCell>
                <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(h.overall_score)}</TableCell>
                <TableCell><RiskPill level={h.risk_level} /></TableCell>
                <TableCell sx={{ maxWidth: 380 }}>
                  {(h.recommendations || []).length === 0 ? <Box sx={{ fontSize: 11.5, color: "#94A3B8" }}>—</Box> : (
                    <Box component="ul" sx={{ m: 0, pl: 2.2, fontSize: 11.5, color: "#4B5563", lineHeight: 1.55 }}>
                      {h.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Cross-scenario comparison */}
      <Paper sx={{ p: 2.4, mt: 2 }} data-testid="scenario-comparison-card">
        <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
          Cross-scenario shift
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
          {["current", "2030", "2050"].map((sc) => {
            const x = scoresAll[sc];
            return (
              <Box key={sc} sx={{ p: 1.6, border: "1px solid #E2E8F0", borderRadius: 1 }}>
                <Box className="cb-kpi-label">{sc.toUpperCase()}</Box>
                <Box className="cb-mono" sx={{ fontSize: 24, fontWeight: 600, color: "#111827", mt: 0.3 }}>
                  {x ? fmtScore(x.overall_score) : "—"}
                </Box>
                <Box sx={{ mt: 0.4 }}>{x ? <RiskPill level={x.risk_level} /> : "—"}</Box>
                <Box className="cb-mono" sx={{ fontSize: 11.5, color: "#4B5563", mt: 0.4 }}>
                  {x ? `${fmtINR(x.financial_exposure_inr)} · ${fmtBps(x.pd_uplift_bps)}` : "Re-score to populate"}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </DashboardLayout>
  );
}

function Meta({ label, value, mono, icon, link }) {
  return (
    <Box>
      <Box className="cb-kpi-label" sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
        {icon}{label}
      </Box>
      <Box className={mono ? "cb-mono" : "cb-sans"} sx={{ fontSize: 13, fontWeight: 600, color: "#111827", mt: 0.3 }}>
        {link ? <a href={link} style={{ color: "#1E2A38", textDecoration: "none", borderBottom: "1px dotted #94A3B8" }}>{value}</a> : value}
      </Box>
    </Box>
  );
}
